import { useCallback, useEffect, useRef, useState } from "react";
import { message, Spin } from "antd";
import DashboardFilterBar from "./components/DashboardFilterBar";
import KPICard from "../../components/common/KPICard";
import {
  SUMMARY_KPI_DEFS,
  ALL_VALUE,
  defaultSummaryFilters,
} from "./constants";
import { getDashboardSummary } from "../../services/dashboard.service";

const EMPTY_KPIS = SUMMARY_KPI_DEFS.reduce(
  (acc, kpi) => ({ ...acc, [kpi.key]: null }),
  {}
);

/** Strip the "All" sentinel and comma-join zones for the API. */
function toQueryParams(filters) {
  const params = {};
  if (filters.year && filters.year !== ALL_VALUE) params.year = filters.year;
  // A month with no year is not a range; the filter bar keeps these in step.
  if (params.year && filters.month && filters.month !== ALL_VALUE) {
    params.month = filters.month;
  }
  if (filters.project && filters.project !== ALL_VALUE) params.project = filters.project;
  if (filters.fleetType && filters.fleetType !== ALL_VALUE) params.fleetType = filters.fleetType;
  if (filters.city && filters.city !== ALL_VALUE) params.city = filters.city;
  if (filters.rider && filters.rider !== ALL_VALUE) params.rider = filters.rider;
  if (filters.zones?.length) params.zones = filters.zones.join(",");
  return params;
}

export default function SummaryTab() {
  const [filters, setFilters] = useState(defaultSummaryFilters);
  const [kpis, setKpis] = useState(EMPTY_KPIS);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  // Overlapping requests: discard a response whose filters are already stale.
  const request = useRef(0);

  const load = useCallback(async (activeFilters) => {
    const requestId = (request.current += 1);
    setLoading(true);
    try {
      const data = await getDashboardSummary(toQueryParams(activeFilters));
      if (requestId !== request.current) return;
      setKpis({ ...EMPTY_KPIS, ...(data?.kpis || {}) });
      setMeta(data?.meta || null);
    } catch {
      if (requestId !== request.current) return;
      setKpis(EMPTY_KPIS);
      setMeta(null);
      message.error("Failed to load summary data");
    } finally {
      if (requestId === request.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  const handleRefresh = () => {
    setFilters(defaultSummaryFilters());
  };

  // Only warn when the gap is real for the current selection.
  const ordersGap = meta?.depositsMissingOrdersCount > 0;

  return (
    <div className="dash-tab">
      <DashboardFilterBar
        values={filters}
        onChange={setFilters}
        onRefresh={handleRefresh}
        showYearMonth
        yearMonthFirst
        showAllOption
        multiZone
        allowAllPeriod
        showPartners
        singlePartner
      />

      <div className="dash-body">
        {ordersGap && (
          <div className="dash-period-notice">
            {meta.depositsMissingOrdersCount} of {meta.nonApprovedDeposits} pending
            or rejected deposits have no order count recorded — the rider-side
            order tiles below under-report until those are filled in.
          </div>
        )}

        <Spin spinning={loading}>
          <div className="dash-kpi-grid cols-4">
            {SUMMARY_KPI_DEFS.map((kpi) => (
              <KPICard
                key={kpi.key}
                label={kpi.label}
                value={kpis[kpi.key]}
                unit={kpi.unit}
                allowNegative={kpi.allowNegative}
                caveat={kpi.caveat}
              />
            ))}
          </div>
        </Spin>
      </div>
    </div>
  );
}
